import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { Archive, Edit, Link as LinkIcon } from "@mui/icons-material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Collaborator, sendCollaboratorSSOLink, softDeleteCollaborator } from "src/apis/company";
import NoDataAlert from "src/pages/components/NoDataAlert";
import { Id } from "src/utils/types";

type CompanyCollaboratorsTableProps = {
  collaborators: Collaborator[];
  companyId: Id;
  reloadList: () => Promise<void>;
  /** ID da empresa de arquivo (destino do soft delete) */
  archiveTargetCompanyId?: number;
};

const CompanyCollaboratorsTable: FC<CompanyCollaboratorsTableProps> = ({
  collaborators,
  companyId,
  reloadList,
  archiveTargetCompanyId,
}): JSX.Element => {
  const navigate = useNavigate();
  const hasCollaborators = useMemo(
    () => Boolean(collaborators) && collaborators.length > 0,
    [collaborators]
  );

  const ARCHIVE_TARGET_ID = Number(archiveTargetCompanyId ?? 45);

  const [entries, setEntries] = useState(collaborators);
  const [selected, setSelected] = useState<Collaborator | null>(null);
  const [archiving, setArchiving] = useState(false);

  const [ssoTarget, setSsoTarget] = useState<Collaborator | null>(null);
  const [ssoEmail, setSsoEmail] = useState('');
  const [ssoSending, setSsoSending] = useState(false);
  const [ssoSuccess, setSsoSuccess] = useState(false);

  const handleOpenSso = useCallback((collab: Collaborator) => {
    setSsoTarget(collab);
    setSsoEmail(collab.email ?? '');
    setSsoSuccess(false);
  }, []);

  const handleCloseSso = useCallback(() => {
    if (ssoSending) return;
    setSsoTarget(null);
    setSsoEmail('');
    setSsoSuccess(false);
  }, [ssoSending]);

  const handleSendSsoLink = useCallback(async () => {
    if (!ssoTarget || !ssoEmail.trim()) return;
    try {
      setSsoSending(true);
      await sendCollaboratorSSOLink(companyId, ssoTarget.id_colaborador, ssoEmail.trim());
      setSsoSuccess(true);
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || 'Erro ao enviar link SSO.');
    } finally {
      setSsoSending(false);
    }
  }, [ssoTarget, ssoEmail, companyId]);

  useEffect(() => {
    if (hasCollaborators) {
      setEntries(collaborators);
    }
  }, [hasCollaborators, collaborators]);

  const removeEntry = useCallback((entryId: Id) => {
    setEntries((current) => current?.filter((e) => e.id_colaborador !== entryId));
  }, []);

  const handleOpenArchive = useCallback((collab: Collaborator) => {
    setSelected(collab);
  }, []);

  const handleCloseArchive = useCallback(() => {
    if (archiving) return;
    setSelected(null);
  }, [archiving]);

  const handleConfirmArchive = useCallback(async () => {
    if (!selected) return;
    try {
      setArchiving(true);
      await softDeleteCollaborator(companyId, selected.id_colaborador, ARCHIVE_TARGET_ID);
      removeEntry(selected.id_colaborador);
      await reloadList();
      setSelected(null);
    } catch (err: any) {
      alert(err?.message || "Erro ao arquivar colaborador.");
    } finally {
      setArchiving(false);
    }
  }, [selected, companyId, ARCHIVE_TARGET_ID, removeEntry, reloadList]);

  if (!hasCollaborators) {
    return <NoDataAlert />;
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Nome</strong></TableCell>
              <TableCell><strong>Setor</strong></TableCell>
              <TableCell><strong>Cargo</strong></TableCell>
              <TableCell><strong>Unidade</strong></TableCell>
              <TableCell><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries?.map((collaborator: Collaborator) => (
              <TableRow key={collaborator?.id_colaborador}>
                <TableCell>{collaborator?.id_colaborador}</TableCell>
                <TableCell>{collaborator?.nome}</TableCell>
                <TableCell>{collaborator?.setor}</TableCell>
                <TableCell>{collaborator?.cargo}</TableCell>
                <TableCell>{collaborator?.unidade}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/colaboradores/${collaborator?.id_colaborador}/editar`)
                        }
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Enviar link de cadastro SSO">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenSso(collaborator)}
                        color="secondary"
                      >
                        <LinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Arquivar">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenArchive(collaborator)}
                      >
                        <Archive fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(selected)} onClose={handleCloseArchive} maxWidth="sm" fullWidth>
        <DialogTitle>Arquivar colaborador</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja arquivar <strong>{selected?.nome}</strong> (ID {selected?.id_colaborador})? O
            colaborador deixará de aparecer na lista. Esta ação não exclui os dados.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseArchive} disabled={archiving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmArchive}
            disabled={archiving}
            startIcon={<Archive />}
          >
            {archiving ? "Arquivando..." : "Arquivar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(ssoTarget)} onClose={handleCloseSso} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar link de cadastro SSO</DialogTitle>
        <DialogContent>
          {ssoSuccess ? (
            <DialogContentText color="success.main">
              Link enviado com sucesso para <strong>{ssoEmail}</strong>.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText gutterBottom>
                Informe o e-mail de <strong>{ssoTarget?.nome}</strong> para enviar o link de
                cadastro vinculado à empresa.
              </DialogContentText>
              <TextField
                label="E-mail do colaborador"
                type="email"
                fullWidth
                size="small"
                value={ssoEmail}
                onChange={(e) => setSsoEmail(e.target.value)}
                disabled={ssoSending}
                sx={{ mt: 1 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseSso} disabled={ssoSending}>
            {ssoSuccess ? "Fechar" : "Cancelar"}
          </Button>
          {!ssoSuccess && (
            <Button
              variant="contained"
              onClick={handleSendSsoLink}
              disabled={ssoSending || !ssoEmail.trim()}
              startIcon={<LinkIcon />}
            >
              {ssoSending ? "Enviando…" : "Enviar link"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyCollaboratorsTable;
